import { RailTrackResponseDto } from '@planning/app/api'
import {
  carrierVisitScheduleDefaultValues,
  ICarrierVisitSchedule,
} from '@planning/components/CarrierVisitSchedule'
import {
  validateEtaDate,
  validateEtaDatetime,
  validateEtdDate,
  validateEtdDatetime,
  validateReasonableDate,
} from '@planning/utils/visit-timing-utils'
import { FieldStore, FormStore, requiredArray, requiredDate, validateSubForm } from '@tom-ui/utils'
import { Dayjs } from 'dayjs'
import { action, makeObservable } from 'mobx'
import { RailVisitScheduleFormStore } from './RailVisitScheduleFormStore'

export type RailVisitType = 'singleVisit' | 'repeatingVisit'

export type RailVisitTrackAndTimingFormStoreType = {
  etaDate: Dayjs | null
  etaTime: Dayjs | null
  etdDate: Dayjs | null
  etdTime: Dayjs | null
  visitType: RailVisitType | null
  schedule?: ICarrierVisitSchedule | null
  railcarEstimate: number | null
  railTracks?: RailTrackResponseDto[] | null
}

export class RailVisitTrackAndTimingFormStore extends FormStore<RailVisitTrackAndTimingFormStoreType> {
  etaDate: FieldStore<Dayjs | null>
  etaTime: FieldStore<Dayjs | null>
  etdDate: FieldStore<Dayjs | null>
  etdTime: FieldStore<Dayjs | null>

  visitType: FieldStore<RailVisitType | null>
  schedule: RailVisitScheduleFormStore

  railcarEstimate: FieldStore<number | ''>

  railTracks: FieldStore<RailTrackResponseDto[] | null | undefined>

  constructor(initialValues?: Partial<RailVisitTrackAndTimingFormStoreType>) {
    super(initialValues)

    makeObservable(this, {
      setData: action,
      reset: action,
    })

    this.etaDate = new FieldStore<Dayjs | null>(
      initialValues?.etaDate ?? null,
      (val): string | undefined =>
        validateReasonableDate(val) ||
        validateEtaDate({
          etaDate: val,
          etdDate: this.etdDate.value,
        }) ||
        requiredDate()(val),
    )
    this.etaTime = new FieldStore<Dayjs | null>(
      initialValues?.etaTime ?? null,
      (val): string | undefined =>
        validateEtaDatetime({
          etaTime: val,
          etdTime: this.etdTime.value,
          etaDate: this.etaDate.value,
          etdDate: this.etdDate.value,
        }) || requiredDate()(val),
    )

    this.etdDate = new FieldStore<Dayjs | null>(
      initialValues?.etdDate ?? null,
      (val): string | undefined =>
        validateReasonableDate(val) ||
        validateEtdDate({
          etaDate: this.etaDate.value,
          etdDate: val,
        }) ||
        requiredDate()(val),
    )
    this.etdTime = new FieldStore<Dayjs | null>(
      initialValues?.etdTime ?? null,
      (val): string | undefined =>
        validateEtdDatetime({
          etaTime: this.etaTime.value,
          etdTime: val,
          etaDate: this.etaDate.value,
          etdDate: this.etdDate.value,
        }) || requiredDate()(val),
    )
    this.visitType = new FieldStore<RailVisitType | null>(initialValues?.visitType ?? 'singleVisit')
    this.schedule = new RailVisitScheduleFormStore(
      initialValues?.schedule ?? carrierVisitScheduleDefaultValues,
    )
    this.railTracks = new FieldStore<RailTrackResponseDto[] | null | undefined>(
      initialValues?.railTracks ?? [],
      requiredArray,
    )
    this.railcarEstimate = new FieldStore<number | ''>(initialValues?.railcarEstimate ?? '')
  }

  setData(data: Partial<RailVisitTrackAndTimingFormStoreType>) {
    this.etaDate.setValue(data.etaDate ?? null)
    this.etaTime.setValue(data.etaTime ?? null)
    this.etdDate.setValue(data.etdDate ?? null)
    this.etdTime.setValue(data.etdTime ?? null)
    this.visitType.setValue(data.visitType ?? 'singleVisit')
    this.schedule.setData(data.schedule ?? carrierVisitScheduleDefaultValues)
    this.railcarEstimate.setValue(data.railcarEstimate ?? '')
    this.railTracks.setValue(data.railTracks ?? [])
  }

  get data(): RailVisitTrackAndTimingFormStoreType {
    return {
      etaDate: this.etaDate.value,
      etaTime: this.etaTime.value,
      etdDate: this.etdDate.value,
      etdTime: this.etdTime.value,

      visitType: this.visitType.value,
      schedule: this.schedule.data,

      railcarEstimate: this.railcarEstimate.value
        ? parseInt(this.railcarEstimate.value.toString())
        : 0,
      railTracks: this.railTracks.value,
    }
  }

  get isDirty(): boolean {
    return (
      this.etaDate.dirty ||
      this.etaTime.dirty ||
      this.etdDate.dirty ||
      this.etdTime.dirty ||
      this.visitType.dirty ||
      this.schedule.isDirty ||
      this.railcarEstimate.dirty
    )
  }

  validate = () => {
    return validateSubForm(this)
  }

  reset() {
    this.setData(this.initialValues)
  }
}
